import SLog from '@/shared/lib/SLog'

function sanitizeDownloadFileNamePart(value: string) {
    return value.replace(/[\\/:*?"<>|]+/g, '_').trim()
}

function splitDownloadFileName(fileName: string) {
    const trimmed = fileName.trim()
    const lastDotIndex = trimmed.lastIndexOf('.')

    if (lastDotIndex <= 0 || lastDotIndex === trimmed.length - 1) {
        return {
            baseName: trimmed,
            extension: '',
        }
    }

    return {
        baseName: trimmed.slice(0, lastDotIndex),
        extension: trimmed.slice(lastDotIndex + 1),
    }
}

function resolveDownloadFileNameFromUrl(url: string, fallbackBaseName = 'download') {
    if (url.startsWith('data:') || url.startsWith('blob:')) {
        return sanitizeDownloadFileNamePart(fallbackBaseName) || 'download'
    }

    try {
        const parsed = new URL(url)
        if (parsed.pathname.endsWith('/')) {
            return sanitizeDownloadFileNamePart(fallbackBaseName) || 'download'
        }

        const lastPathSegment = decodeURIComponent(parsed.pathname.split('/').filter(Boolean).pop() ?? '')
        const { baseName, extension } = splitDownloadFileName(lastPathSegment)
        const resolvedBaseName = sanitizeDownloadFileNamePart(baseName || fallbackBaseName) || 'download'

        if (!extension) {
            return resolvedBaseName
        }

        return `${resolvedBaseName}.${sanitizeDownloadFileNamePart(extension)}`
    } catch {
        return sanitizeDownloadFileNamePart(fallbackBaseName) || 'download'
    }
}

function resolveTextDownloadFileName(fileName: string) {
    const { baseName } = splitDownloadFileName(fileName)
    const resolvedBaseName = sanitizeDownloadFileNamePart(baseName || fileName || 'text') || 'text'
    return `${resolvedBaseName}.txt`
}

function createDownloadError(status?: number) {
    const suffix = status ? `（HTTP ${status}）` : ''
    return new Error(`下载失败，请检查网络设置后重试${suffix}`)
}

export async function downloadRemoteFile(url: string, fallbackBaseName = 'download') {
    if (!url || typeof document === 'undefined' || typeof window === 'undefined') {
        SLog.logEvent('download', 'fail msg-无效的链接或环境');
        return
    }

    try {
        const downloadFileName = resolveDownloadFileNameFromUrl(url, fallbackBaseName)
        let downloadUrl = url

        // 对于 OSS 链接，利用 response-content-disposition 强制触发原生下载
        // 避免使用 fetch + blob 读取整个文件到内存，导致 Mac 端等大文件下载失败
        if (url.startsWith('http://') || url.startsWith('https://')) {
            try {
                const urlObj = new URL(url)
                urlObj.searchParams.set('response-content-disposition', `attachment;filename=${encodeURIComponent(downloadFileName)}`)
                downloadUrl = urlObj.toString()
            } catch (e) {
                // ignore
            }
        }

        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = downloadFileName
        link.rel = 'noopener'
        document.body.appendChild(link)
        link.click()
        link.remove()

        SLog.logEvent('download', 'suc');
    } catch (err) {
        const error = err instanceof Error && err.message.startsWith('下载失败') ? err : createDownloadError()
        SLog.logEvent('download', `fail msg-${error.message}`);
        throw error;
    }
}

export function downloadTextFile(text: string, fileName = 'text') {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
        SLog.logEvent('download', 'fail msg-无效的环境');
        return
    }

    try {
        const fileBlob = new Blob([text], { type: 'text/plain;charset=utf-8' })
        const objectUrl = URL.createObjectURL(fileBlob)
        const downloadFileName = resolveTextDownloadFileName(fileName)

        try {
            const link = document.createElement('a')
            link.href = objectUrl
            link.download = downloadFileName
            link.rel = 'noopener'
            document.body.appendChild(link)
            link.click()
            link.remove()
        } finally {
            window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0)
        }

        SLog.logEvent('download', 'suc');
    } catch (err) {
        SLog.logEvent('download', `fail msg-${err instanceof Error ? err.message : '未知错误'}`);
        throw err;
    }
}
